/** The human sentence inside a provider error, however deeply it's buried.
 *
 *  Local engines wrap errors in layers — LM Studio streams
 *  `{"error":{"message":"Engine protocol predict request returned 500: {\"error\":{\"message\":\"…\\nError:
 *  Jinja Exception: System message must be at the beginning.\"}}"}}` — and showing that raw produced the
 *  reported banner full of JSON and template dumps. Unwrap nested JSON tails, and for a template dump
 *  keep only its final "Error: …" line. rejectedSystemPlacement matches on the RESULT too (the sentence
 *  survives the unwrap), so the shape-retry keys off the same cleaned message. */
export function unwrapErrorMessage(err: unknown): string {
  let msg = typeof err === "string" ? err : ((err as { message?: string })?.message ?? "stream error");
  for (let i = 0; i < 3; i++) { // bounded: each layer is one gateway wrapping another
    const tail = msg.match(/\{[\s\S]*\}\s*$/);
    if (!tail) break;
    try {
      const j = JSON.parse(tail[0]) as { error?: { message?: string } | string; message?: string };
      const inner = typeof j.error === "string" ? j.error : (j.error?.message ?? j.message);
      if (typeof inner === "string" && inner.trim()) { msg = inner; continue; }
    } catch { /* not a clean JSON tail */ }
    break;
  }
  if (/jinja|raise_exception|while executing/i.test(msg)) {
    // The LAST "Error: …" line is the human sentence; it can be followed by the wrapper's own JSON
    // remnants (the nested envelope holds real newlines inside quoted strings, so it never parses as
    // JSON above) — stop at the first quote to leave those behind.
    const all = [...msg.matchAll(/Error:\s*(?:Jinja Exception:\s*)?([^\n"]+)/g)];
    const last = all[all.length - 1];
    if (last) return `The model's chat template rejected the request: ${last[1].trim()}`;
  }
  return msg.trim();
}

/** An upstream failure, carrying the status code (and any retry-after) so callers can react, e.g. auto-retry
 *  with a fallback model on an overload. `.message` is already the human-friendly string. A failure that
 *  arrived as a 200 body or SSE frame (`{"error": …}`) is one too, with status 200 (`errorFrame`).
 *  `dailyLimit`: the AICC proxy's per-user daily token budget is spent (see dailyLimitMessage); waiting or
 *  switching model cannot help, so callers must not retry or fall back. `code`: the stable reason a proxy
 *  sends (`error.code` or a top-level `code`: PROXY_ERROR_CODES.md and the Rin Chat subscription's own, such
 *  as `period_limit`); null when the body names none. `resetsAt`: when a spent allowance comes back.
 *  `requestId`: the server's id for this call, to quote in a bug report, when it sent one. */
export class InferenceError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly retryAfterSeconds: number | null = null,
    readonly dailyLimit = false,
    readonly code: string | null = null,
    readonly resetsAt: string | null = null,
    readonly requestId: string | null = null,
  ) {
    super(message);
    this.name = "InferenceError";
  }
}

/** A failure delivered INSIDE a 200: an SSE `data: {"error": …}` frame, or a whole 200 body that is
 *  `{"error": …}` (after any leading whitespace: api.rin.chat keeps a long call alive with spaces, then fails
 *  that way). Keeps the error's `code` and `retryAfterSec`, so `busy` is waited out and `upstream_unavailable`
 *  retried, exactly as when the same failure comes back with a real status. `message` overrides the text. */
export function errorFrame(err: unknown, message?: string): InferenceError {
  const o = err && typeof err === "object" ? (err as { code?: unknown; retryAfterSec?: unknown; resetsAt?: unknown; requestId?: unknown }) : {};
  const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
  const wait = typeof o.retryAfterSec === "number" && Number.isFinite(o.retryAfterSec) && o.retryAfterSec >= 0 ? o.retryAfterSec : null;
  return new InferenceError(200, message ?? unwrapErrorMessage(err), wait, false, str(o.code), str(o.resetsAt), str(o.requestId));
}

/** One OpenAI tool/function call, assembled from the (incrementally streamed) tool_calls deltas. */
export interface ToolCall {
  id: string;
  type: "function";
  function: { name: string; arguments: string }; // arguments is a JSON STRING
}

/** Parse an OpenAI-compatible SSE byte/text stream into content deltas. */
export interface StreamChunk {
  delta: string;
  done: boolean;
  /** OpenAI finish_reason of the last choice ("stop", "length", …) — carried on the final chunk. */
  finishReason?: string;
  /**
   * Reasoning/thinking text streamed in its OWN field rather than inline in `content`. Gateways differ:
   * NanoGPT/DeepSeek use `reasoning_content`, OpenRouter uses `reasoning`, some use `thinking`. Without
   * reading these the model's chain-of-thought is silently dropped (no Thinking block ever appears).
   */
  reasoning?: string;
  /** Function tool calls the model made this turn — assembled across deltas, carried on the final chunk. */
  toolCalls?: ToolCall[];
}

/** The connection closed after reply text arrived but before the stream said it was finished: no [DONE] and
 *  no finish_reason. The reply was cut off in transit, not ended by the model, and ending the stream quietly
 *  saved the fragment as if it were the whole reply. */
export class StreamCutError extends Error {
  constructor() {
    super("The connection closed before the reply finished.");
    this.name = "StreamCutError";
  }
}

export async function* parseSSE(
  chunks: AsyncIterable<string | Uint8Array>
): AsyncIterable<StreamChunk> {
  const decoder = new TextDecoder();
  let buffer = "";
  let finishReason: string | undefined;
  /** Reply text arrived: a stream that then just stops, with no [DONE] and no finish_reason, was cut off. */
  let sawText = false;
  // tool_calls stream incrementally: OpenAI sends one array element per call keyed by `index`, with id/name
  // on the first delta for that index and the JSON arguments string split across later deltas. Accumulate.
  const toolAcc = new Map<number, ToolCall>();
  const toolCallsOut = (): ToolCall[] | undefined =>
    toolAcc.size ? [...toolAcc.entries()].sort((a, b) => a[0] - b[0]).map(([, c]) => c) : undefined;
  const doneChunk = (): StreamChunk => ({ delta: "", done: true, finishReason, toolCalls: toolCallsOut() });

  // Handle one SSE line: yields content deltas and returns true if it was the terminal [DONE].
  function* handleLine(raw: string): Generator<StreamChunk, boolean> {
    const line = raw.trim();
    if (!line.startsWith("data:")) return false; // blank/comment/event: lines
    const data = line.slice(5).trim();
    if (data === "[DONE]") return true;
    let json: any;
    try {
      json = JSON.parse(data);
    } catch {
      return false; // partial JSON or keepalive — ignore (parse only; never swallow the checks below)
    }
    // Some gateways (OpenRouter, NanoGPT, …) return HTTP 200 and stream the failure as a data frame,
    // e.g. `data: {"error":{"message":"rate limited"}}`. Surface it instead of ending with a blank reply.
    if (json.error) {
      throw errorFrame(json.error);
    }
    const choice = json.choices?.[0];
    if (choice?.finish_reason) finishReason = choice.finish_reason;
    const d = choice?.delta;
    // Accumulate any tool_calls deltas (see toolAcc). Kept separate from content — emitted on the done chunk.
    if (Array.isArray(d?.tool_calls)) {
      for (const tc of d.tool_calls) {
        // Without `index` (some gateways send each call whole), a call with a NEW id is a new call —
        // defaulting to 0 merged two calls into one unparseable argument string.
        let i: number;
        if (typeof tc.index === "number") i = tc.index;
        else {
          const known = tc.id ? [...toolAcc.entries()].find(([, c]) => c.id === tc.id)?.[0] : undefined;
          i = known ?? toolAcc.size;
        }
        const acc = toolAcc.get(i) ?? { id: "", type: "function" as const, function: { name: "", arguments: "" } };
        if (tc.id) acc.id = tc.id;
        if (tc.function?.name) acc.function.name = tc.function.name;
        if (typeof tc.function?.arguments === "string") acc.function.arguments += tc.function.arguments;
        if (!acc.id) acc.id = `call_${i}`; // some gateways omit the id — synthesize a stable one
        toolAcc.set(i, acc);
      }
    }
    // Reasoning may arrive in a dedicated field (see StreamChunk.reasoning). Accept every spelling
    // in use; `reasoning` can also be an object ({text}/{content}) on some gateways.
    const rawReason = d?.reasoning_content ?? d?.reasoning ?? d?.thinking;
    const reasoning: string =
      typeof rawReason === "string" ? rawReason : (rawReason?.text ?? rawReason?.content ?? "");
    if (reasoning) yield { delta: "", done: false, reasoning };
    const delta: string = d?.content ?? "";
    if (delta) { sawText = true; yield { delta, done: false }; }
    return false;
  }

  for await (const raw of chunks) {
    buffer += typeof raw === "string" ? raw : decoder.decode(raw, { stream: true });
    let nl: number;
    while ((nl = buffer.indexOf("\n")) !== -1) {
      const line = buffer.slice(0, nl);
      buffer = buffer.slice(nl + 1);
      if (yield* handleLine(line)) { yield doneChunk(); return; }
    }
  }
  // Stream closed: flush pending multibyte bytes, then process a final line with no trailing newline
  // (an abrupt close or a server that doesn't newline-terminate the last event / [DONE]).
  buffer += decoder.decode();
  const tail = buffer.trim();
  if (tail && !tail.startsWith("data:") && !tail.startsWith(":") && !tail.startsWith("event:")) {
    // A 200 whose body is NOT SSE at all: a gateway JSON error without the `data:` prefix, an HTML
    // error page, or a server that ignored `stream: true` and sent one whole completion. Ignoring it
    // ended the stream as a blank reply with no error — the real message was simply lost.
    let json: any = null;
    try { json = JSON.parse(tail); } catch { /* not JSON — fall through */ }
    if (json && typeof json === "object") {
      if (json.error) throw errorFrame(json.error);
      const msg = json.choices?.[0]?.message;
      const text = typeof msg?.content === "string" ? msg.content : "";
      if (text) { yield { delta: text, done: false }; if (json.choices?.[0]?.finish_reason) finishReason = json.choices[0].finish_reason; }
    } else if (/<html|<!doctype/i.test(tail)) {
      throw new Error("The provider answered with a web page instead of a completion — check the URL (it usually needs /v1) and that the service is up.");
    }
    yield doneChunk();
    return;
  }
  if (tail && (yield* handleLine(buffer))) { yield doneChunk(); return; }
  if (sawText && !finishReason) throw new StreamCutError();
  yield doneChunk();
}
